import { initAxios } from '../../../utils/axiosHelper';
import {
  BulkUploadResponse,
  CheckDuplicateResponse,
  CreateEntityResponse,
  IEntity,
  PaginatedResponse,
  PaginationParams,
} from './entities';
import { StatusFlag } from './statusFlags';

// The lens filter matches the stored lens names exactly, and those are the saved
// lens text split on , ; | with each name trimmed (entity_hub_lens_array).
const toStoredLensNames = (lenses: string[]): string[] =>
  Array.from(
    new Set(
      lenses
        .flatMap((lens) => lens.split(/[,;|]+/))
        .map((lens) => lens.trim())
        .filter(Boolean),
    ),
  );

// Get entities by database ID
export const getEntitiesByDatabaseId = async (
  id?: string,
  params?: Partial<PaginationParams>,
): Promise<PaginatedResponse<IEntity>> => {
  try {
    if (!id) {
      return {
        data: [],
        pagination: {
          currentPage: 1,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: 10,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }

    const instance = await initAxios();
    if (!instance) {
      return {
        data: [],
        pagination: {
          currentPage: 1,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: 10,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }

    const queryParams = new URLSearchParams({
      page: (params?.page || 1).toString(),
      limit: (params?.limit || 10).toString(),
      sortBy: params?.sortBy || 'hit_count',
      sortOrder: params?.sortOrder || 'desc',
      search: params?.search || '',
      tab: params?.tab || 'All',
    });

    if (params?.statusFilters?.length) {
      params.statusFilters.forEach((status) => queryParams.append('statusFilters', status));
    }

    if (params?.riskFilters?.length) {
      params.riskFilters.forEach((risk) => queryParams.append('riskFilters', risk));
    }

    if (params?.lensFilters?.length) {
      toStoredLensNames(params.lensFilters).forEach((lens) =>
        queryParams.append('lensFilters', lens),
      );
    }

    if (params?.hitCountRanges?.length) {
      params.hitCountRanges.forEach((range) => queryParams.append('hitCountRanges', range));
    }

    if (params?.nameStartsWith?.length) {
      params.nameStartsWith.forEach((letter) => queryParams.append('nameStartsWith', letter));
    }

    if (params?.entitySearchTerms?.length) {
      params.entitySearchTerms.forEach((term) => {
        const trimmed = term.trim();
        if (trimmed) {
          queryParams.append('entitySearch', trimmed);
        }
      });
    }

    if (params?.hasNewResults) {
      queryParams.set('hasNewResults', params.hasNewResults);
    }

    if (params?.statusReset) {
      queryParams.set('statusReset', 'true');
    }

    if (params?.previousStatus) {
      queryParams.set('previousStatus', params.previousStatus);
    }

    if (params?.impactedByError) {
      queryParams.set('impactedByError', params.impactedByError);
    }

    const resp = await instance.get(`/bite-db/entities/database/${id}?${queryParams.toString()}`);

    return {
      data: resp.data?.data || [],
      pagination: resp.data?.pagination || {
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  } catch (err) {
    console.error('Error fetching entities:', err);
    return {
      data: [],
      pagination: {
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  }
};

// Get all entities by database ID (for export)
export const getAllEntitiesByDatabaseId = async (id?: string): Promise<IEntity[]> => {
  try {
    if (!id) {
      return [];
    }

    const instance = await initAxios();
    if (!instance) {
      return [];
    }

    const resp = await instance.get(`/bite-db/entities/database/${id}/all`);

    return resp.data?.data || [];
  } catch (err) {
    console.error('Error fetching all entities:', err);
    return [];
  }
};

export const getEntitiesByGroupId = async (
  id?: string,
  params?: Partial<PaginationParams>,
): Promise<PaginatedResponse<IEntity>> => {
  try {
    if (!id) {
      return {
        data: [],
        pagination: {
          currentPage: 1,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: 10,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }

    const instance = await initAxios();
    if (!instance) {
      return {
        data: [],
        pagination: {
          currentPage: 1,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: 10,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }

    const queryParams = new URLSearchParams({
      page: (params?.page || 1).toString(),
      limit: (params?.limit || 10).toString(),
      sortBy: params?.sortBy || 'hit_count',
      sortOrder: params?.sortOrder || 'desc',
      search: params?.search || '',
      databaseName: params?.databaseName || '',
    });

    if (params?.exclusions && params.exclusions.length > 0) {
      params.exclusions.forEach((exclusion) => {
        queryParams.append('exclusions', exclusion);
      });
    }

    if (params?.statusFilters?.length) {
      params.statusFilters.forEach((status) => queryParams.append('statusFilters', status));
    }

    if (params?.riskFilters?.length) {
      params.riskFilters.forEach((risk) => queryParams.append('riskFilters', risk));
    }

    if (params?.lensFilters?.length) {
      toStoredLensNames(params.lensFilters).forEach((lens) =>
        queryParams.append('lensFilters', lens),
      );
    }

    if (params?.hitCountRanges?.length) {
      params.hitCountRanges.forEach((range) => queryParams.append('hitCountRanges', range));
    }

    if (params?.nameStartsWith?.length) {
      params.nameStartsWith.forEach((letter) => queryParams.append('nameStartsWith', letter));
    }

    if (params?.entitySearchTerms?.length) {
      params.entitySearchTerms.forEach((term) => {
        const trimmed = term.trim();
        if (trimmed) {
          queryParams.append('entitySearch', trimmed);
        }
      });
    }

    if (params?.hasNewResults) {
      queryParams.set('hasNewResults', params.hasNewResults);
    }

    if (params?.statusReset) {
      queryParams.set('statusReset', 'true');
    }

    if (params?.previousStatus) {
      queryParams.set('previousStatus', params.previousStatus);
    }

    if (params?.createdSince) {
      queryParams.set('createdSince', params.createdSince);
    }

    const resp = await instance.get(`/bite-db/entities/group/${id}?${queryParams.toString()}`);

    return {
      data: resp.data?.data || [],
      pagination: resp.data?.pagination || {
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  } catch (err) {
    console.error('Error fetching entities:', err);
    return {
      data: [],
      pagination: {
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  }
};

// Get entities by group ID
// export const getEntitiesByGroupId = async (
//   groupId?: string,
//   options?: Partial<PaginationParams>,
// ): Promise<{ data: IEntity[]; pagination: any }> => {
//   try {
//     if (!groupId) {
//       return {
//         data: [],
//         pagination: { totalItems: 0, totalPages: 0, currentPage: 1 },
//       };
//     }

//     const instance = await initAxios();
//     if (!instance) {
//       return {
//         data: [],
//         pagination: { totalItems: 0, totalPages: 0, currentPage: 1 },
//       };
//     }

//     // Build query parameters
//     const params = new URLSearchParams();

//     if (options?.page) {
//       params.append('page', options.page.toString());
//     }
//     if (options?.limit) {
//       params.append('limit', options.limit.toString());
//     }
//     if (options?.sortBy) {
//       params.append('sortBy', options.sortBy);
//     }
//     if (options?.sortOrder) {
//       params.append('sortOrder', options.sortOrder);
//     }
//     if (options?.search) {
//       params.append('search', options.search);
//     }
//     if (options?.databaseName) {
//       params.append('databaseName', options.databaseName);
//     }

//     const queryString = params.toString();
//     const url = `/bite-db/entities/group/${groupId}${
//       queryString ? `?${queryString}` : ''
//     }`;

//     const resp = await instance.get(url);

//     return {
//       data: resp.data?.data || [],
//       pagination: resp.data?.pagination || {
//         totalItems: 0,
//         totalPages: 0,
//         currentPage: 1,
//       },
//     };
//   } catch (err) {
//     console.error('Error fetching entities by group ID:', err);
//     return {
//       data: [],
//       pagination: { totalItems: 0, totalPages: 0, currentPage: 1 },
//     };
//   }
// };

// Get entity by ID
export const getEntityById = async (
  id?: string,
  databaseId?: string,
  selectedTeamId?: string,
): Promise<IEntity | null> => {
  try {
    if (!id) {
      throw Error('No ID provided');
    }
    const instance = await initAxios();
    if (!instance) {
      throw Error('Failed to initialize request');
    }

    const hasAssociationContext = Boolean(databaseId && selectedTeamId);
    const resp = await instance.get(
      hasAssociationContext
        ? `/bite-db/database/${databaseId}/entity/${id}`
        : `/bite-db/entity/${id}`,
      hasAssociationContext
        ? { headers: { 'x-selected-team-id': selectedTeamId } }
        : undefined,
    );
    const entityData = resp.data?.data || resp.data?.result;
    if (entityData) {
      return {
        ...entityData,
        type: 'supplier',
      };
    }
    return null;
  } catch (err) {
    throw err;
  }
};

export const getEntityChildren = async (
  entityId: string,
  databaseId: string,
  selectedTeamId: string,
): Promise<Array<IEntity & { relationship_type: string | null; has_children: boolean }>> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return [];
    }
    const resp = await instance.get(
      `/bite-db/entity/${entityId}/children?databaseId=${databaseId}`,
      { headers: { 'x-selected-team-id': selectedTeamId } },
    );
    return resp.data?.data || [];
  } catch (err) {
    console.error('Error fetching entity children:', err);
    return [];
  }
};

export const getEntityHierarchy = async (
  id?: string,
  options?: {
    direction?: 'above' | 'below' | 'both';
    maxDepth?: number;
  },
): Promise<{ above?: IEntity[]; below?: IEntity[] }> => {
  try {
    if (!id) {
      return {};
    }
    const instance = await initAxios();
    if (!instance) {
      return {};
    }

    const direction = options?.direction ?? 'both';
    const maxDepth = options?.maxDepth ?? 20;
    const resp = await instance.get(
      `/bite-db/entity/${id}/hierarchy?direction=${direction}&maxDepth=${maxDepth}`,
    );
    return resp.data?.data || {};
  } catch (err) {
    return {};
  }
};

// Update entity
export const updateEntity = async (
  id: string,
  entityData: Partial<any>,
): Promise<{ success: boolean; data?: any; message?: string }> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return { success: false, message: 'Failed to initialize API client' };
    }

    const resp = await instance.put(`/bite-db/entity/${id}`, JSON.stringify(entityData));
    return { success: true, data: resp.data?.data };
  } catch (err: any) {
    return {
      success: false,
      message: err.response?.data?.message || 'Failed to update entity',
    };
  }
};

// Update entity status
export const updateEntityStatus = async (
  id: string,
  statusData: { status_flag: string },
  selectedTeamId: string,
): Promise<boolean> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return false;
    }

    const resp = await instance.put(
      `/bite-db/entity-status/${id}`,
      JSON.stringify(statusData),
      { headers: { 'x-selected-team-id': selectedTeamId } },
    );
    return resp.data?.success || false;
  } catch (err) {
    return false;
  }
};

// Remove from Database: ends the given Database Entities (memberships) in the team, in one request
export const removeDatabaseEntities = async (
  databaseEntityIds: string[],
  selectedTeamId: string,
): Promise<{ removed: number; not_found: number; total_requested: number }> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      throw new Error('Failed to initialize API client');
    }
    if (!selectedTeamId) {
      throw new Error('Team context is required');
    }

    const resp = await instance.delete('/bite-db/database-entities-bulk', {
      data: { database_entity_ids: databaseEntityIds },
      headers: { 'x-selected-team-id': selectedTeamId },
    });
    return resp.data?.data;
  } catch (err: any) {
    throw new Error(
      err.response?.data?.message || 'Failed to remove entities from the database',
    );
  }
};

// Selection status: moves the given Database Entities, from any of the team's Databases, to one Status Flag
export const updateDatabaseEntitiesStatus = async (
  databaseEntityIds: string[],
  toStatus: StatusFlag,
  selectedTeamId: string,
): Promise<{ updatedCount: number; not_found: number; total_requested: number }> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      throw new Error('Failed to initialize API client');
    }
    if (!selectedTeamId) {
      throw new Error('Team context is required');
    }

    const resp = await instance.put(
      '/bite-db/database-entities-bulk/status',
      { database_entity_ids: databaseEntityIds, to_status: toStatus },
      { headers: { 'x-selected-team-id': selectedTeamId } },
    );
    return resp.data?.data;
  } catch (err: any) {
    throw new Error(err.response?.data?.message || 'Failed to update entities status');
  }
};

// Get suppliers by product ID
export const getSuppliersByProductId = async (productId?: string): Promise<IEntity[]> => {
  try {
    if (!productId) {
      return [];
    }
    const instance = await initAxios();
    if (!instance) {
      return [];
    }

    const resp = await instance.get(`/bite-db/supplier-products/product/${productId}`);
    return resp.data?.data || [];
  } catch (err) {
    return [];
  }
};

// Get products by supplier ID
export const getProductsBySupplierId = async (supplierId?: string): Promise<any[]> => {
  try {
    if (!supplierId) {
      return [];
    }
    const instance = await initAxios();
    if (!instance) {
      return [];
    }

    const resp = await instance.get(`/bite-db/supplier-products/supplier/${supplierId}`);
    return resp.data?.data || [];
  } catch (err) {
    return [];
  }
};

// Create/Add a single entity
export const createEntity = async (
  entityData: any,
  teamId?: string,
): Promise<CreateEntityResponse> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return {
        success: false,
        data: null,
      };
    }

    const resp = await instance.post<CreateEntityResponse>(
      '/bite-db/add-supplier',
      JSON.stringify(entityData),
      {
        headers: {
          'x-group-id': teamId || '',
        },
      },
    );
    return resp.data;
  } catch (err) {
    throw err;
  }
};

export const checkEntityDuplicate = async (
  payload: { name: string; country_of_origin?: string; database_id: string },
  teamId?: string,
): Promise<CheckDuplicateResponse> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return {
        success: false,
        duplicates: [],
      };
    }

    const resp = await instance.post<CheckDuplicateResponse>(
      '/bite-db/entity/check-duplicate',
      JSON.stringify(payload),
      {
        headers: {
          'x-group-id': teamId || '',
        },
      },
    );
    return resp.data;
  } catch (err) {
    throw err;
  }
};

// Bulk upload entities
export const bulkUploadEntities = async (
  formData: FormData,
  teamId?: string,
): Promise<BulkUploadResponse> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return {
        success: false,
        data: [],
      };
    }

    const resp = await instance.post<BulkUploadResponse>(
      '/bite-db/add-supplier-bulk-upload',
      formData,
      {
        headers: {
          'x-group-id': teamId || '',
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return resp.data;
  } catch (err) {
    throw err;
  }
};

export const bulkUploadEntitiesNoCheck = async (
  formData: FormData,
  teamId?: string,
): Promise<BulkUploadResponse> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return {
        success: false,
        data: [],
      };
    }

    const resp = await instance.post<BulkUploadResponse>(
      '/bite-db/add-supplier-bulk-upload-no-duplicate-check',
      formData,
      {
        headers: {
          'x-group-id': teamId || '',
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return resp.data;
  } catch (err) {
    throw err;
  }
};

// Bulk create entities WITH server-side duplicate detection (mirrors the CSV
// upload flow). Non-duplicates are inserted; any entities that already exist in
// the database are returned in `duplicates` for review instead of being added.
export const createBulkEntities = async (
  entities: Partial<IEntity>[],
  databaseId: string,
  createdBy?: string,
  teamId?: string,
): Promise<{
  success: boolean;
  data: Array<{
    id: string;
    link_id?: string;
    name: string;
    database_id?: string;
    database_entity_id?: string;
  }>;
  duplicates?: Array<{
    entity: any;
    message?: string;
    existing_entity_id?: string;
    existing_database_entity_id?: string;
  }>;
}> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return {
        success: false,
        data: [],
      };
    }

    const payload = {
      queries: entities,
      database_id: databaseId,
      created_by: createdBy,
    };

    const resp = await instance.post('/bite-db/entities-bulk', payload, {
      headers: {
        'x-group-id': teamId || '',
      },
    });
    return resp.data;
  } catch (err) {
    throw err;
  }
};

// Bulk create entities without duplicate check
export const createBulkEntitiesWithoutDuplicateCheck = async (
  entities: Partial<IEntity>[],
  databaseId: string,
  createdBy?: string,
  teamId?: string,
  uploadStartTime?: string,
): Promise<{
  success: boolean;
  data: Array<{
    id: string;
    link_id?: string;
    name: string;
    database_id?: string;
    database_entity_id?: string;
  }>;
}> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return {
        success: false,
        data: [],
      };
    }

    const payload = {
      queries: entities,
      database_id: databaseId,
      created_by: createdBy,
      uploadStartTime: uploadStartTime,
    };

    const resp = await instance.post('/bite-db/entities-bulk-no-duplicate-check', payload, {
      headers: {
        'x-group-id': teamId || '',
      },
    });
    return resp.data;
  } catch (err) {
    throw err;
  }
};

// Client-side filtering function for entities
export const filterEntities = (entities: IEntity[], searchTerm: string): IEntity[] => {
  if (!searchTerm || searchTerm.trim() === '') {
    return entities;
  }

  const lowerSearchTerm = searchTerm.toLowerCase().trim();

  return entities.filter((entity) => {
    const name = entity.name?.toLowerCase() || '';
    const description = entity.description?.toLowerCase() || '';
    const category = entity.category?.toLowerCase() || '';
    const email = entity.email?.toLowerCase() || '';
    const address = entity.address?.toLowerCase() || '';
    const country = entity.country_of_origin?.toLowerCase() || '';

    return (
      name.includes(lowerSearchTerm) ||
      description.includes(lowerSearchTerm) ||
      category.includes(lowerSearchTerm) ||
      email.includes(lowerSearchTerm) ||
      address.includes(lowerSearchTerm) ||
      country.includes(lowerSearchTerm)
    );
  });
};

// Update entities status from one status to another
export const updateEntitiesStatusFromTo = async (
  databaseId: string,
  scope: string,
  toStatus: string,
  selectedTeamId: string,
  fromStatus?: string,
  entityIds?: string[],
  tab?: string,
  riskLevel?: string,
): Promise<{ success: boolean; message: string; count: number }> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      throw new Error('Failed to initialize API client');
    }
    if (!selectedTeamId) {
      throw new Error('Team context is required');
    }

    const resp = await instance.put(
      `/bite-db/entities/database/${databaseId}/status/transition`,
      {
        scope: scope,
        to_status: toStatus,
        from_status: fromStatus,
        entity_ids: entityIds,
        tab: tab,
        risk_level: riskLevel,
      },
      { headers: { 'x-selected-team-id': selectedTeamId } },
    );
    return resp.data;
  } catch (err: any) {
    throw new Error(err.response?.data?.message || 'Failed to update entities status');
  }
};

export type EntityStatusTransitionPreview = {
  would_update: number;
  barred_excluded: number;
};

export const previewEntitiesStatusFromTo = async (
  databaseId: string,
  scope: string,
  toStatus: string,
  selectedTeamId: string,
  fromStatus?: string,
  entityIds?: string[],
  tab?: string,
  riskLevel?: string,
): Promise<EntityStatusTransitionPreview> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      throw new Error('Failed to initialize API client');
    }
    if (!selectedTeamId) {
      throw new Error('Team context is required');
    }

    const resp = await instance.put(
      `/bite-db/entities/database/${databaseId}/status/transition/preview`,
      {
        scope: scope,
        to_status: toStatus,
        from_status: fromStatus,
        entity_ids: entityIds,
        tab: tab,
        risk_level: riskLevel,
      },
      { headers: { 'x-selected-team-id': selectedTeamId } },
    );
    const preview = resp.data?.data;
    return {
      would_update: preview?.would_update ?? 0,
      barred_excluded: preview?.barred_excluded ?? 0,
    };
  } catch (err: any) {
    throw new Error(err.response?.data?.message || 'Failed to preview entities status');
  }
};

export const restorePreviousStatus = async (
  ids: string[],
  selectedTeamId: string,
): Promise<{ success: boolean; message: string; data?: { updatedCount: number } }> => {
  const instance = await initAxios();
  if (!instance) {
    throw new Error('Failed to initialize API client');
  }
  if (!selectedTeamId) {
    throw new Error('Team context is required');
  }
  const resp = await instance.put(
    '/bite-db/entities/previous-status/restore',
    { ids: ids },
    { headers: { 'x-selected-team-id': selectedTeamId } },
  );
  return resp.data;
};

export const declinePreviousStatus = async (
  ids: string[],
  selectedTeamId: string,
): Promise<{ success: boolean; message: string; data?: { updatedCount: number } }> => {
  const instance = await initAxios();
  if (!instance) {
    throw new Error('Failed to initialize API client');
  }
  if (!selectedTeamId) {
    throw new Error('Team context is required');
  }
  const resp = await instance.put(
    '/bite-db/entities/previous-status/decline',
    { ids: ids },
    { headers: { 'x-selected-team-id': selectedTeamId } },
  );
  return resp.data;
};

export const restorePreviousStatusByFlag = async (payload: {
  databaseId?: string;
  groupId?: string;
  previous_status_flag: string;
  selectedTeamId: string;
}): Promise<{ success: boolean; message: string; data?: { updatedCount: number } }> => {
  const instance = await initAxios();
  if (!instance) {
    throw new Error('Failed to initialize API client');
  }
  if (!payload.selectedTeamId) {
    throw new Error('Team context is required');
  }
  const { selectedTeamId, ...body } = payload;
  const resp = await instance.put(
    '/bite-db/entities/previous-status/restore-by-flag',
    body,
    { headers: { 'x-selected-team-id': selectedTeamId } },
  );
  return resp.data;
};
